import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import Seo from "@/components/Seo";
import Reveal from "@/components/Reveal";
import { overline } from "@/components/kit";
import { CITY_DATA, CITY_IMAGES } from "@/data/cities";
import { CityProjects, CityCTA } from "@/components/CityShared";
import CityLocalBusinessJsonLd from "@/components/CityLocalBusinessJsonLd";

// MARSEILLE — Full-bleed image hero + horizontal highlights
export default function CityMarseille() {
  const c = CITY_DATA.marseille;
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["0%", "30%"]);

  return (
    <>
      <Seo title={`Événementiel à ${c.name}`} description={c.seoDesc} path="/villes/marseille" />
      <CityLocalBusinessJsonLd city={c} />
      <section ref={ref} className="relative flex h-screen min-h-[600px] items-center justify-center overflow-hidden text-center">
        <motion.img style={{ y }} src={CITY_IMAGES.marseille} alt="Marseille — le Vieux-Port" className="absolute inset-0 h-[130%] w-full object-cover" />
        <div className="absolute inset-0 bg-black/45" />
        <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-black/20" />
        <div className="relative z-10 px-5">
          <span className="text-[11px] uppercase tracking-[0.28em] font-semibold text-lime">{c.region}</span>
          <motion.h1 initial={{ scale: 1.1, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} transition={{ duration: 1.1, ease: [0.22, 1, 0.36, 1] }} className="mt-6 font-serif text-7xl font-medium leading-none tracking-tight text-white md:text-[11rem]">{c.name}</motion.h1>
          <p className="mx-auto mt-6 max-w-2xl text-lg text-white/85">{c.tagline}</p>
        </div>
      </section>

      <section className="mx-auto max-w-4xl px-5 py-20 md:px-10 md:py-28 text-center">
        <Reveal><p className="font-serif text-3xl leading-snug text-ink md:text-4xl">{c.intro}</p></Reveal>
        <div className="mt-12 space-y-8 text-left">{c.body.map((p, i) => (<Reveal key={i} delay={i}><p className="text-lg leading-relaxed text-ink/65">{p}</p></Reveal>))}</div>
      </section>

      <section className="border-y border-ink/10 bg-mist">
        <div className="mx-auto flex max-w-[1440px] flex-col gap-px overflow-hidden px-5 py-4 md:flex-row md:px-10">
          {c.highlights.map((h, i) => (
            <Reveal key={i} delay={i} className="flex-1">
              <div className="p-8 md:border-r md:border-ink/10 md:last:border-0">
                <h3 className="font-serif text-2xl text-lime-dim">{h.k}</h3>
                <p className="mt-3 text-sm text-ink/60">{h.v}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      <div className="mx-auto max-w-[1440px] px-5 pt-14 md:px-10"><p className="text-sm uppercase tracking-[0.2em] text-ink/40">{c.quartiers.join("  ·  ")}</p></div>

      <CityProjects city={c.name} />
      <CityCTA city={c.name} />
    </>
  );
}
